import { useState } from 'react'
import { X, Upload, FileText } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'

export default function YTImportModal({ onClose, onComplete }) {
  const { api } = useAuth()
  const [csvText, setCsvText] = useState('')
  const [parsed, setParsed] = useState(null)
  const [importing, setImporting] = useState(false)
  const [status, setStatus] = useState('')

  // RFC 4180 compliant CSV parser - handles multi-line quoted fields
  function parseCSV(text) {
    const rows = []
    let headers = null
    let currentRow = []
    let currentField = ''
    let inQuotes = false
    let i = 0

    while (i < text.length) {
      const ch = text[i]

      if (inQuotes) {
        if (ch === '"') {
          // Check for escaped quote ""
          if (i + 1 < text.length && text[i + 1] === '"') {
            currentField += '"'
            i += 2
            continue
          }
          // End of quoted field
          inQuotes = false
          i++
          continue
        }
        currentField += ch
        i++
      } else {
        if (ch === '"' && currentField === '') {
          // Start of quoted field
          inQuotes = true
          i++
        } else if (ch === ',') {
          // Field separator
          currentRow.push(currentField.trim())
          currentField = ''
          i++
        } else if (ch === '\r') {
          // Skip carriage return
          i++
        } else if (ch === '\n') {
          // End of row
          currentRow.push(currentField.trim())
          currentField = ''

          if (!headers) {
            headers = currentRow
          } else if (currentRow.some(f => f !== '')) {
            // Build row object
            const obj = {}
            headers.forEach((h, idx) => {
              obj[h] = currentRow[idx] || ''
            })
            if (obj['Video ID']) {
              rows.push(obj)
            }
          }
          currentRow = []
          i++
        } else {
          currentField += ch
          i++
        }
      }
    }

    // Handle last row (no trailing newline)
    if (currentField !== '' || currentRow.length > 0) {
      currentRow.push(currentField.trim())
      if (headers && currentRow.some(f => f !== '')) {
        const obj = {}
        headers.forEach((h, idx) => {
          obj[h] = currentRow[idx] || ''
        })
        if (obj['Video ID']) {
          rows.push(obj)
        }
      }
    }

    return rows
  }

  function handleParse() {
    const rows = parseCSV(csvText)
    if (!rows || rows.length === 0) {
      setStatus('Could not parse CSV. Ensure it has a header row with "Video ID" column.')
      return
    }
    setParsed(rows)

    // Show diagnostic: check field population
    const withChannel = rows.filter(r => r['Channel ID']).length
    const withViloud = rows.filter(r => r['Viloud Channel Name']).length
    setStatus(`Parsed ${rows.length} rows (${withChannel} with Channel ID, ${withViloud} with Viloud)`)
  }

  function handleFile(e) {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target.result
      setCsvText(text)
      const rows = parseCSV(text)
      if (rows && rows.length > 0) {
        const withChannel = rows.filter(r => r['Channel ID']).length
        const withViloud = rows.filter(r => r['Viloud Channel Name']).length
        setParsed(rows)
        setStatus(`Parsed ${rows.length} rows from ${file.name} (${withChannel} with Channel ID, ${withViloud} with Viloud)`)
      }
    }
    reader.readAsText(file)
  }

  async function handleImport() {
    if (!parsed || parsed.length === 0) return
    setImporting(true)
    try {
      const data = await api('/youtube/import-csv', {
        method: 'POST',
        body: JSON.stringify({ rows: parsed })
      })
      const errMsg = data.errors ? `, ${data.errors} errors` : ''
      setStatus(`✅ Imported ${data.imported}, skipped ${data.skipped}${errMsg}`)
      setTimeout(onComplete, 1500)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setImporting(false)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="yt-import-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2><Upload size={16} /> Import YouTube Data from CSV</h2>
          <button className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>

        {status && <div className={`yt-detail-status ${status.startsWith('Error') ? 'error' : ''}`}>{status}</div>}

        <div className="yt-import-content">
          <p className="muted">
            Import from Google Sheets export. Handles multi-line descriptions.
            Expected columns: Event Type, Title, Video ID, Channel ID, Viloud Channel Name, etc.
          </p>

          <div className="yt-import-file">
            <label className="btn-ghost" style={{ cursor: 'pointer' }}>
              <FileText size={14} /> Choose CSV File
              <input type="file" accept=".csv,.tsv,.txt" onChange={handleFile} style={{ display: 'none' }} />
            </label>
          </div>

          <textarea
            className="yt-import-textarea"
            value={csvText}
            onChange={e => { setCsvText(e.target.value); setParsed(null) }}
            placeholder="Or paste CSV data here..."
            rows={10}
          />

          {!parsed && csvText && (
            <button className="btn-ghost" onClick={handleParse}>Parse CSV</button>
          )}

          {parsed && (
            <div className="yt-import-preview">
              <h3>{parsed.length} rows ready to import</h3>
              <div className="yt-import-sample">
                {parsed.slice(0, 5).map((row, i) => (
                  <div key={i} className="yt-import-row">
                    <span className={`yt-event-badge ${(row['Event Type'] || '').toLowerCase()}`}>
                      {row['Event Type'] || '?'}
                    </span>
                    <span className="yt-import-title">{row['Title'] || row.title || '—'}</span>
                    <span className="muted" style={{ flexShrink: 0 }}>{row['Video ID']}</span>
                    {row['Channel ID'] ? (
                      <span className="yt-linked-badge" style={{ flexShrink: 0 }}>CH ✓</span>
                    ) : (
                      <span className="yt-event-badge live" style={{ flexShrink: 0 }}>No CH</span>
                    )}
                  </div>
                ))}
                {parsed.length > 5 && <p className="muted">...and {parsed.length - 5} more</p>}
              </div>

              <button className="btn-primary" onClick={handleImport} disabled={importing}>
                <Upload size={14} /> {importing ? 'Importing...' : `Import ${parsed.length} Videos`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
